import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import PageHeader from "../../../../components/common/PageHeader";
import PageLoader from "../../../../components/common/PageLoader";
import ChipList from "../../../../components/common/ChipList";
import { ROLE_SCOPES } from "../../constants";
import { systemRoleService, tenantRoleService } from "../../services";
import {
  collectAllowSuggestions,
  filterAllowByCatalog,
  isRoleObjectId,
  normalizeRole,
  normalizeRoleList,
  pickRoleId,
} from "../../utils/normalizeRole";
import RoleFormDialog from "../RoleFormDialog";
import { tenantPermissionService } from "../../../permission-management/services";
import { normalizePermissionList } from "../../../permission-management/utils/normalizePermission";
import {
  systemClientService,
  tenantClientService,
} from "../../../client-management/services";
import { normalizeClientList } from "../../../client-management/utils/normalizeClient";
import {
  systemUserService,
  tenantUserService,
} from "../../../user-management/services";
import { appActions } from "../../../../redux/actions";
import { getApiErrorMessage } from "../../../../helpers";
import useAuth from "../../../../hooks/useAuth";

const DetailItem = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const RoleDetailsPage = ({ scope = ROLE_SCOPES.TENANT }) => {
  const isSystem = scope === ROLE_SCOPES.SYSTEM;
  const { roleId: roleParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { activeTenantId, tenantId: authTenantId } = useAuth();
  const tenantId = activeTenantId || authTenantId;
  const listPath = isSystem ? "/roles" : "/tenant-roles";

  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [allRoles, setAllRoles] = useState([]);
  const [linkedClients, setLinkedClients] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);

  // Tenant: live permission catalog only. System: suggest from existing roles.
  const allowSuggestions = useMemo(() => {
    if (!isSystem) {
      return Array.from(
        new Set((permissionOptions || []).filter(Boolean))
      ).sort();
    }
    return collectAllowSuggestions(allRoles);
  }, [allRoles, isSystem, permissionOptions]);

  const visibleAllow = useMemo(() => {
    if (isSystem || !role) return role?.allow || [];
    return filterAllowByCatalog(role.allow, permissionOptions);
  }, [isSystem, permissionOptions, role]);

  const loadPermissionOptions = useCallback(async () => {
    if (isSystem) {
      setPermissionOptions([]);
      return;
    }
    setPermissionsLoading(true);
    try {
      const payload = await tenantPermissionService.getAllTenantPermissions(
        tenantId
      );
      setPermissionOptions(
        normalizePermissionList(payload)
          .map((item) => item.name)
          .filter(Boolean)
      );
    } catch {
      setPermissionOptions([]);
    } finally {
      setPermissionsLoading(false);
    }
  }, [isSystem, tenantId]);

  useEffect(() => {
    if (!isSystem) {
      loadPermissionOptions();
    }
  }, [isSystem, loadPermissionOptions]);

  const loadRolesForSuggestions = useCallback(async () => {
    if (allRoles.length) return;
    try {
      const payload = isSystem
        ? await systemRoleService.getAllSystemRoles()
        : await tenantRoleService.getAllTenantRoles(tenantId);
      setAllRoles(normalizeRoleList(payload));
    } catch {
      setAllRoles([]);
    }
  }, [allRoles.length, isSystem, tenantId]);

  const findRoleInList = useCallback(
    async (decoded, fromState) => {
      const payload = isSystem
        ? await systemRoleService.getAllSystemRoles()
        : await tenantRoleService.getAllTenantRoles(tenantId);
      const list = normalizeRoleList(payload);
      setAllRoles(list);
      return (
        list.find(
          (item) =>
            String(item.roleId) === decoded ||
            String(item.name) === decoded ||
            (fromState?.name && String(item.name) === String(fromState.name))
        ) || null
      );
    },
    [isSystem, tenantId]
  );

  const loadRole = useCallback(async () => {
    if (!roleParam) return;
    setLoading(true);
    setError("");
    try {
      const decoded = decodeURIComponent(roleParam);
      const fromState = location.state?.role
        ? normalizeRole(location.state.role)
        : null;
      let detail = null;
      let listMatch = null;

      if (fromState && !isRoleObjectId(fromState.roleId)) {
        // State came without a real id — refresh from Get All.
        listMatch = await findRoleInList(decoded, fromState);
      } else if (fromState && isRoleObjectId(fromState.roleId)) {
        listMatch = fromState;
      }

      if (isSystem) {
        const nameHint = fromState?.name || listMatch?.name || decoded;
        try {
          detail = await systemRoleService.getSystemRoleByName(nameHint);
        } catch {
          detail = null;
        }
        if (!detail && !listMatch) {
          listMatch = await findRoleInList(decoded, fromState);
          if (listMatch?.name) {
            try {
              detail = await systemRoleService.getSystemRoleByName(
                listMatch.name
              );
            } catch {
              detail = listMatch;
            }
          }
        }
        if (!detail) detail = listMatch || fromState;
        if (!detail) throw new Error("Role not found.");
      } else {
        // Tenant: load details by name when possible; keep Role Id from Get All.
        const nameHint = fromState?.name || listMatch?.name || "";
        if (isRoleObjectId(decoded)) {
          try {
            detail = await tenantRoleService.getTenantRoleById(
              decoded,
              tenantId
            );
          } catch {
            detail = null;
          }
        }
        if (!detail) {
          const byName =
            nameHint || (!isRoleObjectId(decoded) ? decoded : "");
          if (byName) {
            try {
              detail = await tenantRoleService.getTenantRoleByName(
                byName,
                tenantId
              );
            } catch {
              detail = null;
            }
          }
        }
        if (!listMatch && (!detail || !pickRoleId(detail, fromState))) {
          listMatch = await findRoleInList(decoded, fromState || detail);
        }
        if (!detail) detail = listMatch || fromState;
        if (!detail) throw new Error("Role not found.");
      }

      const merged = normalizeRole({
        ...(listMatch || {}),
        ...(fromState || {}),
        ...(detail || {}),
      });
      const resolvedId = pickRoleId(listMatch, fromState, detail, merged);
      if (resolvedId) {
        merged.roleId = resolvedId;
        merged.id = resolvedId;
      }

      setRole(merged);
    } catch (err) {
      setRole(null);
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [findRoleInList, isSystem, location.state, roleParam, tenantId]);

  useEffect(() => {
    loadRole();
  }, [loadRole]);

  /** Update/Delete always use Role Id from Get All (or resolved list). */
  const resolveRoleId = useCallback(async () => {
    const fromRole = pickRoleId(role);
    if (fromRole) return fromRole;

    const fromNav = pickRoleId(location.state?.role);
    if (fromNav) return fromNav;

    if (!role?.name) return "";

    try {
      const payload = isSystem
        ? await systemRoleService.getAllSystemRoles()
        : await tenantRoleService.getAllTenantRoles(tenantId);
      const list = normalizeRoleList(payload);
      setAllRoles(list);
      const match =
        list.find(
          (item) =>
            String(item.name).toLowerCase() ===
            String(role.name).toLowerCase()
        ) || null;
      return pickRoleId(match);
    } catch {
      return "";
    }
  }, [isSystem, location.state, role, tenantId]);

  const openEdit = async () => {
    setEditOpen(true);
    setPermissionOptions([]);
    if (isSystem) {
      await loadRolesForSuggestions();
      return;
    }
    await loadPermissionOptions();
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      const id = await resolveRoleId();
      if (!id) {
        throw new Error(
          "Role id is missing. Refresh the roles list and open the role again."
        );
      }
      if (isSystem) {
        await systemRoleService.updateSystemRole(id, payload);
      } else {
        await tenantRoleService.updateTenantRole(id, payload, tenantId);
      }
      dispatch(appActions.apiSuccessMessage("Role updated successfully."));
      setEditOpen(false);
      const nextRole = {
        ...role,
        roleId: id,
        id,
        name: role?.name,
        allow: payload.allow || payload.permissions || role?.allow,
      };
      setRole(normalizeRole(nextRole));
      // Keep resolved Role Id in URL/state for subsequent update/delete.
      navigate(`${listPath}/view/${encodeURIComponent(id)}`, {
        replace: true,
        state: { role: nextRole },
      });
    } catch (err) {
      throw new Error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const openDelete = async () => {
    setDeleteOpen(true);
    setLinkedClients([]);
    setLinksLoading(true);
    try {
      const roleName = String(role?.name || "").trim().toLowerCase();
      if (!roleName) {
        setLinkedClients([]);
        return;
      }
      const payload = isSystem
        ? await systemClientService.getAllSystemClients()
        : await tenantClientService.getAllTenantClients(tenantId);
      const clients = normalizeClientList(payload);
      setLinkedClients(
        clients
          .filter((client) =>
            (client.roles || []).some(
              (item) => String(item).trim().toLowerCase() === roleName
            )
          )
          .map((client) =>
            String(client.name || client.clientId || client.id || "").trim()
          )
          .filter(Boolean)
      );
    } catch {
      setLinkedClients([]);
    } finally {
      setLinksLoading(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const id = await resolveRoleId();
      if (!id) {
        throw new Error(
          "Role id is missing. Refresh the roles list and open the role again."
        );
      }
      if (isSystem) {
        await systemUserService.detachRoleFromSystemUsers(role);
        await systemClientService.detachRoleFromSystemClients(role);
        await systemRoleService.deleteSystemRole(id);
      } else {
        await tenantUserService.detachRoleFromTenantUsers(role, tenantId);
        await tenantClientService.detachRoleFromTenantClients(role, tenantId);
        await tenantRoleService.deleteTenantRole(id, tenantId);
      }
      dispatch(appActions.apiSuccessMessage("Role deleted successfully."));
      setDeleteOpen(false);
      navigate(listPath);
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader text="Loading role..." />;
  }

  if (error && !role) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={() => navigate(listPath)}
        >
          Back to roles
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!role) return null;

  const canMutate = Boolean(pickRoleId(role, location.state?.role));

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <Button
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={() => navigate(listPath)}
        >
          Back to roles
        </Button>
      </Stack>
      <PageHeader title="Role Details" subtitle={role.name || role.roleId} />

      <Paper
        elevation={0}
        sx={{ p: 3, border: "1px solid", borderColor: "divider", mb: 2 }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <DetailItem label="Role ID" value={role.roleId} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailItem label="Role Name" value={role.name} />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Permissions
            </Typography>
            <ChipList
              items={visibleAllow}
              maxVisible={null}
              emptyLabel="No permissions assigned"
              sx={{ flexWrap: "wrap", rowGap: 0.75, overflow: "visible" }}
            />
          </Box>
        </Stack>
      </Paper>

      {!canMutate ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Role Id is missing for this record. Refresh the roles list and open
          the role again before Edit or Delete.
        </Alert>
      ) : null}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.25}
        flexWrap="wrap"
        useFlexGap
      >
        <Button variant="contained" onClick={openEdit} disabled={!canMutate}>
          Edit Role
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={openDelete}
          disabled={!canMutate}
        >
          Delete Role
        </Button>
      </Stack>

      <RoleFormDialog
        open={editOpen}
        mode="edit"
        role={role}
        allowSuggestions={allowSuggestions}
        permissionsLoading={permissionsLoading}
        loading={saving}
        tenantMode={!isSystem}
        onClose={() => setEditOpen(false)}
        onSubmit={handleSave}
      />

      <Dialog
        open={deleteOpen}
        onClose={saving ? undefined : () => setDeleteOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Delete role <strong>{role.name || role.roleId}</strong>? This cannot
            be undone.
          </Typography>
          {linksLoading ? (
            <Typography variant="body2" color="text.secondary">
              Checking where this role is used...
            </Typography>
          ) : linkedClients.length ? (
            <Alert severity="warning" sx={{ mt: 0.5 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                This role is still assigned to{" "}
                <strong>{linkedClients.length}</strong> client
                {linkedClients.length === 1 ? "" : "s"}. Deleting it may leave
                those clients with a missing or invalid role until they are
                updated.
              </Typography>
              <Typography variant="body2" component="div">
                Associated clients:
                <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
                  {linkedClients.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </Box>
              </Typography>
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No clients currently list this role.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving || linksLoading}
          >
            {saving ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleDetailsPage;
